import { observable, computed, action } from "mobx";
import { GameModel } from "./game.model";
import { ThemeEntity } from "@citychallenge/api-public";
import { Translation } from "../i18n";
import { GameSessionModel } from "../player/game-session/game-session.model";

export interface GameThemeProgress {
  id: string;
  name: string;
  color?: string;
  active: boolean;
  activeLevel?: number;
  activeGame?: GameModel;
  games: GameModel[];
  progress: number;
  image?: string;
}

export class GameStore {
  constructor(games: GameModel[] = []) {
    this.games = games;
  }

  @observable
  public games: GameModel[];

  @computed
  public get visibleGames() {
    return this.games.filter((game) => game.isVisible);
  }

  @computed
  public get themeFilteredGames() {
    if (!this.activeThemes.length) return this.visibleGames;
    return this.visibleGames.filter(
      (game) => game.theme?.id && this.activeThemes.includes(game.theme?.id)
    );
  }

  @computed
  public get gamesWithoutTheme() {
    return this.visibleGames.filter((game) => !game.theme);
  }

  @computed
  public get availableTypes() {
    const types: string[] = [];

    for (const game of this.games) {
      if (!types.includes(game.gameType)) types.push(game.gameType);
    }

    return types;
  }

  @observable
  public activeMapFilter?: string | Translation;

  @action
  public toggleActiveMapFilter = (newFilter?: string | Translation) => {
    this.activeMapFilter = newFilter;
  };

  @computed
  public get filteredGames() {
    if (this.activeMapFilter) {
      return this.games.filter(
        (game) => game.gameType === this.activeMapFilter
      );
    }

    return this.games;
  }

  /**
   * All games that can be displayed on a map
   */
  @computed
  public get coordinateBasedGames() {
    const games = this.games.filter((game) => {
      return !!game.coordinates;
    });

    return games;
  }

  @action
  public setGames(games: GameModel[]) {
    this.games = games;
  }

  @action
  public setSessions(sessions: GameSessionModel[]) {
    for (const session of sessions) {
      const game = this.games.find((g) => g.id === session.id);
      if (game) game.activeSession = session;
    }
  }

  public findGameById(id: string): GameModel | undefined {
    return this.games.find((game) => game.id === id);
  }

  @computed
  public get themes(): GameThemeProgress[] {
    const themes: {
      [key: string]: {
        games: GameModel[];
        theme: ThemeEntity;
        id: string;
      };
    } = {};

    for (const game of this.visibleGames) {
      if (!game.theme) continue;

      const themeId = game.theme.id || game.theme.name;
      const theme = themes[themeId];

      if (theme) theme.games.push(game);
      else
        themes[themeId] = {
          id: themeId,
          theme: game.theme,
          games: [game],
        };
    }

    return Object.entries(themes).map(([_key, theme]) => {
      const totalProgress = theme.games.reduce((prev, curr) => {
        return prev + (curr.activeSession?.progress || 0);
      }, 0);

      const activeGame =
        theme.games.find((game) => game.state !== "completed") ||
        (theme.games.length ? theme.games[theme.games.length - 1] : undefined);

      return {
        id: theme.id,
        name: theme.theme.name,
        image: theme.theme.image,
        activeLevel:
          theme.games.findIndex(({ id }) => id === activeGame?.id) + 1,
        activeGame,
        games: theme.games,
        progress: Math.floor(totalProgress / theme.games.length),
        color: theme.theme.color,
        active: !!this.activeThemes.find((v) => v === theme.id),
      };
    });
  }

  @computed
  public get latestActiveSession() {
    const sessions = this.games
      .filter((game) => {
        return game.activeSession && !game.activeSession.finished;
      })
      .sort((a, b) => {
        return (
          new Date(b.activeSession!.session.started).getTime() -
          new Date(a.activeSession!.session.started).getTime()
        );
      });

    if (!sessions.length) return undefined;

    return sessions[0];
  }

  @computed
  public get nextAvailableGame() {
    return this.visibleGames.find((game) => {
      return !game.activeSession;
    });
  }

  @computed
  public get firstGame() {
    return this.visibleGames.find(() => {
      return true;
    });
  }

  @computed
  public get totalProgress() {
    if (!this.games.length) return 0;

    const totalProgress = this.games.reduce((prev, curr) => {
      return prev + (curr.activeSession?.progress || 0);
    }, 0);

    return Math.floor(totalProgress / this.games.length);
  }

  @observable
  public activeThemes: string[] = [];

  @action
  public toggleActiveTheme = (theme: string) => {
    if (this.activeThemes.includes(theme)) {
      this.activeThemes = this.activeThemes.filter((v) => v !== theme);
    } else {
      this.activeThemes = [...this.activeThemes, theme];
    }
  };
}
